import { clearCacheAfterTransactionMutation } from "@/features/collections/api/clearCacheAfterTransactionMutation";
import type { Currency } from "@/lib/domain/Currency";
import type { PriceSource } from "@/lib/domain/PriceSource";
import { useHTTP, useHTTPMutation, type HTTPResponse } from "@/lib/http";
import { useQueryClient, type MutationFunction } from "@tanstack/react-query";

export interface BulkTransactionsOptions {
	readonly collectionId: string;
	readonly requestModel: BulkTransactionsRequestModel;
}

export interface BulkTransactionsRequestModel {
	readonly currency: Currency;
	readonly priceSource: PriceSource;
	readonly itemsLeaving: BulkTransactionsRequestModelVariantQuantityPair[];
	readonly itemsEntering: BulkTransactionsRequestModelVariantQuantityPair[];
	readonly cashLeaving: number;
	readonly cashEntering: number;
	readonly occurredDate: string;
}

export interface BulkTransactionsRequestModelVariantQuantityPair {
	readonly variantId: string;
	readonly quantity: number;
}

export const useBulkTransactions = () => {
	const http = useHTTP("Required");
	const queryClient = useQueryClient();

	const mutationFn: MutationFunction<HTTPResponse<undefined>, BulkTransactionsOptions> = options => {
		return http<undefined, BulkTransactionsRequestModel>({
			method: "POST",
			url: "/collection/" + options.collectionId + "/operation/bulk-transactions",
			body: options.requestModel
		});
	};

	return useHTTPMutation({
		mutationFn,
		onSuccess: (_, requestProperties) => {
			clearCacheAfterTransactionMutation(queryClient, requestProperties.collectionId);
			queryClient.invalidateQueries({ queryKey: ["transactions", requestProperties.collectionId] });
		}
	});
};
